import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import editaisAdminService from "../../../services/editaisAdminService";
import type {
  EditalTemplate,
  EditalDetalhado,
} from "../../../services/editaisAdminService";

export default function FormEdital() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [templates, setTemplates] = useState<EditalTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] =
    useState<EditalTemplate | null>(null);
  const [edital, setEdital] = useState<EditalDetalhado | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    nome: "",
    descricao: "",
    data_inicio: "",
    data_fim: "",
    edital_template_id: "",
    formacao_minima_requerida: "",
    anexos: [] as File[],
  });

  useEffect(() => {
    const carregarDados = async () => {
      try {
        // Carregar templates
        const templatesResp = await editaisAdminService.listarTemplates();
        console.log("Templates recebidos:", templatesResp);
        setTemplates(templatesResp.templates);

        // Se for edição, carregar edital
        if (id) {
          const editalResp = await editaisAdminService.buscar(parseInt(id));
          console.log("Edital carregado:", editalResp.edital);
          setEdital(editalResp.edital);

          // Pegar o template_id do objeto template se existir
          const templateId =
            editalResp.edital.template?.id?.toString() ||
            editalResp.edital.edital_template_id?.toString() ||
            "";

          setFormData({
            nome: editalResp.edital.nome || "",
            descricao: editalResp.edital.descricao || "",
            data_inicio: editalResp.edital.data_inicio || "",
            data_fim: editalResp.edital.data_fim || "",
            edital_template_id: templateId,
            formacao_minima_requerida: editalResp.edital.formacao_minima_requerida || "",
            anexos: [],
          });

          // Carregar o template do edital para exibir campos
          if (editalResp.edital.template) {
            console.log("Template do edital:", editalResp.edital.template);
            setSelectedTemplate(editalResp.edital.template);
          } else {
            console.log("Edital sem template anexado");
          }
        }
      } catch (err) {
        console.error("Erro ao carregar dados:", err);
        setError("Erro ao carregar dados");
      } finally {
        setLoading(false);
      }
    };

    void carregarDados();
  }, [id]);

  const handleTemplateChange = async (templateId: string) => {
    setFormData({ ...formData, edital_template_id: templateId });

    if (templateId) {
      try {
        // Buscar detalhes do template com schema_campos
        const response = await editaisAdminService.buscarTemplate(
          parseInt(templateId)
        );
        setSelectedTemplate(response.template);
      } catch (err) {
        console.error("Erro ao carregar template:", err);
      }
    } else {
      setSelectedTemplate(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const params = {
        ...formData,
        edital_template_id: parseInt(formData.edital_template_id),
        anexos: formData.anexos.length > 0 ? formData.anexos : undefined,
      };

      if (isEdit && id) {
        await editaisAdminService.atualizar(parseInt(id), params);
        toast.success("Edital atualizado com sucesso!");
        navigate(`/admin/editais/${id}`);
      } else {
        const response = await editaisAdminService.criar(params);
        toast.success("Edital criado com sucesso!");
        navigate(`/admin/editais/${response.edital.id}`);
      }
    } catch (err) {
      toast.error("Erro ao salvar edital");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      {/* Botão Voltar */}
      <div className="mb-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-gray-600 hover:text-gray-900 transition font-medium"
        >
          ← Voltar
        </button>
      </div>

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          {isEdit ? "Editar Edital" : "Novo Edital"}
        </h1>
        <p className="mt-2 text-gray-600">
          {isEdit
            ? "Atualize as informações do edital"
            : "Preencha as informações para criar um novo edital"}
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow p-6 space-y-6"
      >
        {/* Nome */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nome do Edital <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            required
            value={formData.nome}
            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="Ex: Edital FAPITEC/SE Nº 01/2025 - Bolsas IA"
          />
        </div>

        {/* Descrição */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Descrição <span className="text-red-500">*</span>
          </label>
          <textarea
            required
            rows={4}
            value={formData.descricao}
            onChange={(e) =>
              setFormData({ ...formData, descricao: e.target.value })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="Descreva o objetivo e público-alvo do edital..."
          />
        </div>

        {/* Template */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Template <span className="text-red-500">*</span>
          </label>
          <select
            required
            disabled={isEdit}
            value={formData.edital_template_id}
            onChange={(e) => handleTemplateChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
          >
            <option value="">Selecione um template</option>
            {templates?.map((template) => (
              <option key={template.id} value={template.id}>
                {template.nome} v{template.versao}
              </option>
            ))}
          </select>
          {isEdit && (
            <p className="mt-1 text-sm text-gray-500">
              O template não pode ser alterado após a criação
            </p>
          )}
        </div>

        {/* Datas */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data de Início <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              required
              value={formData.data_inicio}
              onChange={(e) =>
                setFormData({ ...formData, data_inicio: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data de Término <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              required
              value={formData.data_fim}
              onChange={(e) =>
                setFormData({ ...formData, data_fim: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Formação Mínima Requerida */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Formação Acadêmica Mínima Requerida
          </label>
          <select
            value={formData.formacao_minima_requerida}
            onChange={(e) =>
              setFormData({ ...formData, formacao_minima_requerida: e.target.value })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Sem requisito de formação</option>
            <option value="graduacao">Graduação</option>
            <option value="especializacao">Especialização</option>
            <option value="mestrado">Mestrado</option>
            <option value="doutorado">Doutorado</option>
            <option value="pos_doutorado">Pós-Doutorado</option>
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Defina o nível mínimo de formação acadêmica exigido para inscrição neste edital.
            Proponentes com formação inferior não poderão se inscrever.
          </p>
        </div>

        {/* PDF */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Anexos do Edital (PDFs, documentos, etc){" "}
            {isEdit && "(adicione novos arquivos ou mantenha os atuais)"}
          </label>
          <input
            type="file"
            multiple
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              setFormData({
                ...formData,
                anexos: files,
              });
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="mt-1 text-xs text-gray-500">
            Selecione um ou mais arquivos (PDFs, DOC, XLS, etc)
          </p>
          {formData.anexos.length > 0 && (
            <div className="mt-2">
              <p className="text-sm font-medium text-gray-700">
                Arquivos selecionados:
              </p>
              <ul className="text-sm text-gray-600 list-disc list-inside">
                {formData.anexos.map((file, index) => (
                  <li key={index}>
                    {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                  </li>
                ))}
              </ul>
            </div>
          )}
          {edital?.anexos && edital.anexos.length > 0 && (
            <div className="mt-2">
              <p className="text-sm font-medium text-gray-700">
                Anexos atuais:
              </p>
              <ul className="text-sm text-gray-600 space-y-1">
                {edital.anexos.map((anexo) => (
                  <li key={anexo.id}>
                    <a
                      href={anexo.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {anexo.filename} (
                      {(anexo.byte_size / 1024 / 1024).toFixed(2)} MB)
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Preview do Formulário que o Proponente verá */}
        {selectedTemplate?.schema_campos && (
          <div className="border-t pt-6 mt-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h3 className="text-lg font-semibold text-blue-900 mb-2 flex items-center gap-2">
                Preview: Como o proponente verá o formulário
              </h3>
              <p className="text-sm text-blue-700">
                Template: <strong>{selectedTemplate.nome}</strong> v
                {selectedTemplate.versao}
              </p>
            </div>

            <div className="space-y-6 bg-white p-6 rounded-lg border-2 border-dashed border-gray-300">
              {Object.entries(selectedTemplate.schema_campos).map(
                ([key, campo]) => (
                  <div key={key}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {campo.label}
                      {campo.required && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                    </label>

                    {/* Renderizar campo baseado no tipo */}
                    {campo.type === "text" && (
                      <input
                        type="text"
                        disabled
                        placeholder={campo.placeholder || ""}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
                      />
                    )}

                    {campo.type === "textarea" && (
                      <textarea
                        disabled
                        rows={4}
                        placeholder={campo.placeholder || ""}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
                      />
                    )}

                    {campo.type === "number" && (
                      <input
                        type="number"
                        disabled
                        placeholder={campo.placeholder || ""}
                        min={campo.min}
                        max={campo.max}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
                      />
                    )}

                    {campo.type === "currency" && (
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-gray-500">
                          R$
                        </span>
                        <input
                          type="number"
                          disabled
                          placeholder={campo.placeholder || "0,00"}
                          min={campo.min}
                          max={campo.max}
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
                        />
                      </div>
                    )}

                    {campo.type === "select" && (
                      <select
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
                      >
                        <option value="">Selecione uma opção</option>
                        {campo.options?.map((opt, idx) => (
                          <option key={idx} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    )}

                    {campo.type === "date" && (
                      <input
                        type="date"
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
                      />
                    )}

                    {campo.type === "url" && (
                      <input
                        type="url"
                        disabled
                        placeholder={campo.placeholder || "https://"}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
                      />
                    )}

                    {/* Info do campo */}
                    <div className="mt-1 flex flex-wrap gap-2 text-xs text-gray-500">
                      {campo.maxLength && (
                        <span>• Máximo: {campo.maxLength} caracteres</span>
                      )}
                      {campo.min !== undefined && (
                        <span>• Mínimo: {campo.min}</span>
                      )}
                      {campo.max !== undefined && (
                        <span>• Máximo: {campo.max}</span>
                      )}
                    </div>
                  </div>
                )
              )}
            </div>

            <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800">
                Este é o formulário que os proponentes preencherão ao se
                inscreverem neste edital
              </p>
            </div>
          </div>
        )}

        {/* Botões */}
        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition disabled:opacity-50 font-medium"
          >
            {submitting
              ? "Salvando..."
              : isEdit
              ? "Atualizar Edital"
              : "Criar Edital"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/editais")}
            className="px-6 py-3 border border-gray-300 rounded-md hover:bg-gray-50 transition"
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}
